import { useEffect, useState } from "react";
import DataPanel from "../../../components/DataPanel/DataPanel";
import Pagination from "../../../components/Pagination/Pagination";
import useModal from "../../../hooks/useModal";
import { useStore } from "../../../store/useStore";
import LimitPerPage from "../../../components/LimitPerPage/LimitPerPage";
import Skeleton from "react-loading-skeleton";
import InfractionRankingTable from "./InfractionRankingTable"
import FilterInfractionModal from "./FilterInfractionModal";
import { INFRACTION_DATE_TYPES, INFRACTION_TYPES } from "../../../utils/constants/constants";
import CalendarNavDay from "../../../components/CalendarNav/CalendarNavDay";
import CalendarNavMonth from "../../../components/CalendarNav/CalendarNavMonth";
import SparklinesChart from "../../../components/SparklinesChart/SparklinesChart";
import StackedBarChart from "../../../components/StackedBarChart/StackedBarChart";
import { InfractionGranularity } from "../../../store/types/infraction";
import SimpleSelect from "../../../components/SimpleSelect/SimpleSelect";
import InfractionCard from "../../../components/InfractionCard/InfractionCard";

const InfractionGeneral: React.FC<{}> = () => {
  const { openModal } = useModal();
  const getInfractionRanking = useStore((state) => state.infraction.getInfractionRanking);
  const getInfractionTypePerPeriodChartData = useStore((state) => state.infraction.getInfractionTypePerPeriodChartData);
  const infractionRankingList = useStore((state) => state.infraction.infractionRankingList);
  const infractionsSummarized: any = useStore((state) => state.infraction.infractionsSummarized);
  const infractionsTypePerPeriodChartData = useStore((state) => state.infraction.infractionsTypePerPeriodChartData);
  const infractionsTypeSparklinesChartData = useStore((state) => state.infraction.infractionsTypeSparklinesChartData);
  const isLoadingInfractionRankingList = useStore((state) => state.infraction.isLoadingInfractionRankingList);
  const isLoadingInfractionsTypePerPeriodChartData = useStore((state) => state.infraction.isLoadingInfractionsTypePerPeriodChartData);
  const hasPermissions = useStore((state) => state.admin.hasPermissions);
  const openFilterInfractionModal = () => openModal(<FilterInfractionModal />);
  const filters = useStore((state) => state.infraction.infractionRankingList.filters);
  const selectedDateType = useStore((state) => state.infraction.selectedDateType);
  const setSelectedDateType = useStore((state) => state.infraction.setSelectedDateType);
  
  useEffect(() => {
    getInfractionRanking();
    getInfractionTypePerPeriodChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="flex justify-end shadow-box">
        <CalendarNavDay
          date={filters.start_date ? new Date(filters.start_date) : new Date()}
          show={selectedDateType === INFRACTION_DATE_TYPES[0].value}
        />
         <CalendarNavMonth
          date={filters.reference_month ? new Date(filters.reference_month) : new Date()}
          show={selectedDateType === INFRACTION_DATE_TYPES[1].value}
        />
        <div className="rounded-[13px] bg-white ml-4 p-3 align-center">
          <div className="flex items-center">
            <SimpleSelect
              className="py-2 bg-white text-lg text-[#7B809A] font-semibold"
              options={[
                { label: "Diário", value: '0' },
                { label: "Mensal", value: '1' },
              ]}
              onChange={(selectedOption) => {
                setSelectedDateType(selectedOption.target.value === '0' ? INFRACTION_DATE_TYPES[0].value : INFRACTION_DATE_TYPES[1].value);
                getInfractionRanking();
                getInfractionTypePerPeriodChartData();
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap justify-evenly items-center gap-3">
        {
          INFRACTION_TYPES.map(({label, value, field} : { label: string, value: string, field: unknown }) => {
            const { total } = infractionsSummarized[0]?.find((item: any) => item.type === value) || { total: 0 };
            const { total: lastTotal } = infractionsSummarized[1]?.find((item: any) => item.type === value) || { total: 0 };

            const difference = (total ?? 0) - (lastTotal ?? 0);
            const percentageDifference = lastTotal !== 0 ? ((difference / lastTotal) * 100) : null;

            let color;
            switch(label) {
              case "Macros Manuais":
                color = "#DA1E27";
                break;

              case "Macros Alteradas":
                color = "#E98D7F";
                break;

              case "Interjornada":
                color = "#C26388";
                break;

              case "Refeição":
                color = "#8B81C3";
                break;

              case "Horas Extras":
                color = "#75A6C2";
                break;

              case "Tempo de Direção":
                color = "#A2A2A2";
                break;

              default:
                color = "#DA1E27";
                break;
            }

            return (
              <InfractionCard 
                title={label}
                value={parseFloat(total.toFixed(2)).toString() ?? '-'}
                percentageDifference={percentageDifference}
                color={color}
                loading={isLoadingInfractionsTypePerPeriodChartData}
                className='w-[50%] sm:w-[33%] lg:w-[19%]'
              />
            )
          })
        }
      </div>
      <div className="my-4 w-full">
          <div className="shadow-base rounded-[8px] bg-white p-4 gap-3 mt-1 w-full">
            <StackedBarChart
              title={selectedDateType === INFRACTION_DATE_TYPES[0].value ? "Infrações - Última Semana" : "Infrações - Último Quadrimestre"}
              data={infractionsTypePerPeriodChartData}
              loading={isLoadingInfractionsTypePerPeriodChartData}
              div='generalchartdiv'
              height={selectedDateType === INFRACTION_DATE_TYPES[0].value ? 450 : 300}
            />
          </div>
      </div>
      <div className="my-4 w-full flex flex-wrap justify-between">
        <DataPanel className="px-3 pt-8 flex flex-col mr-3 mt-3 w-[100%] xl:w-[49%]">
          <div className="flex justify-between items-center mb-4">
            <div className="text-dark-blue font-bold text-2xl ml-3">Ranking de Motoristas</div>
            {/*
              <RoundedFilledButton
                onClick={openFilterInfractionModal}
                icon={<Filter />}
                text="Filtros"
              />
            */}
          </div>
          <InfractionRankingTable />
          <div className="flex justify-between items-center">
            <div className="text-gray_1">
              Mostrando{" "}
              {!isLoadingInfractionRankingList ? (infractionRankingList.data.length + infractionRankingList.offset * infractionRankingList.limitPerPage) : <Skeleton inline width={20} />}{" "}
              de {infractionRankingList.totalItems} registros
            </div>
            <div className="flex gap-5 items-center">
              <LimitPerPage
                onLimitChange={(e: any) =>
                  infractionRankingList.setLimitPerPage(e.target.value)
                }
              />
              <Pagination
                currentPage={infractionRankingList.currentPage}
                totalPages={infractionRankingList.totalPages}
                onPageChange={infractionRankingList.setCurrentPage}
              />
            </div>
          </div>
        </DataPanel>
        <div className="shadow-base rounded-[8px] bg-white mt-3 px-10 pt-5 w-[100%] xl:w-[49%]">
            <SparklinesChart
              title={selectedDateType === INFRACTION_DATE_TYPES[0].value ? "Tendências - Última Semana" : "Tendências - Último Quadrimestre"}
              data={infractionsTypeSparklinesChartData}
              loading={isLoadingInfractionsTypePerPeriodChartData}
              granularity={selectedDateType === INFRACTION_DATE_TYPES[0].value ? InfractionGranularity.day : InfractionGranularity.month}
            />
        </div>
      </div>
    </>
  );
};

export default InfractionGeneral;
